import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ChevronLeft, Trash2, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MedicineSearch from "@/components/dialogs/MedicineSearch";
import MedicationRow, { medFromMedicine } from "@/components/MedicationRow";

export default function RxGroups() {
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/rx-groups");
    setGroups(data);
    if (!activeId && data.length) setActiveId(data[0].id);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const active = groups.find((g) => g.id === activeId);

  const create = async () => {
    if (!newName.trim()) return toast.error("Give the group a name");
    setCreating(true);
    try {
      const { data } = await api.post("/rx-groups", { name: newName.trim(), enabled: true, drugs: [] });
      setNewName("");
      await load();
      setActiveId(data.id);
    } catch (e) { toast.error("Failed to create"); }
    finally { setCreating(false); }
  };

  const updateActive = async (patch) => {
    if (!active) return;
    const updated = { ...active, ...patch };
    setGroups((gs) => gs.map((g) => g.id === active.id ? updated : g));
    try {
      await api.put(`/rx-groups/${active.id}`, { name: updated.name, enabled: !!updated.enabled, drugs: updated.drugs || [] });
    } catch (e) { toast.error("Save failed"); }
  };

  const saveActive = async () => {
    if (!active) return;
    try {
      await api.put(`/rx-groups/${active.id}`, { name: active.name, enabled: !!active.enabled, drugs: active.drugs || [] });
      toast.success("Rx group saved");
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Rx group?")) return;
    await api.delete(`/rx-groups/${id}`);
    if (activeId === id) setActiveId(null);
    await load();
  };

  const addMedicine = (m) => {
    const drug = medFromMedicine(m);
    updateActive({ drugs: [...(active.drugs || []), drug] });
  };

  const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-[#47554D] hover:text-[#0A4C36]"><ChevronLeft size={16} /> Back</button>
        <h1 className="text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Rx Group</h1>
        <div className="w-24" />
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        {/* Left: list */}
        <aside className="md:col-span-4 card-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>Rx Group</div>
            <button className="w-9 h-9 rounded-full bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center" data-testid="rx-new-btn" onClick={() => document.getElementById("rx-new-input")?.focus()}>
              <Plus size={16} />
            </button>
          </div>
          <div className="flex gap-2 mb-3">
            <input id="rx-new-input" data-testid="rx-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New group name (e.g. General Fever)" className="input-clean" onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
            <button className="btn-emerald px-4" disabled={creating || !newName.trim()} onClick={create} data-testid="rx-create-btn">Add</button>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups" className="input-clean mb-3" />
          <div className="space-y-2">
            {filteredGroups.length === 0 && <div className="text-xs text-[#8A9992] py-4 text-center">No groups yet.</div>}
            {filteredGroups.map((g) => (
              <div key={g.id} className={`flex items-center justify-between rounded-2xl px-4 py-3 cursor-pointer border ${activeId === g.id ? "bg-[#E6F0EC] border-[#0A4C36]/20" : "bg-white border-[#EFECE5] hover:bg-[#F3F1EC]"}`}
                onClick={() => setActiveId(g.id)}
                data-testid={`rx-group-item-${g.id}`}
              >
                <div>
                  <div className={`font-semibold text-sm ${activeId === g.id ? "text-[#0A4C36]" : "text-[#1F2A24]"}`}>{g.name}</div>
                  <div className="text-xs text-[#8A9992]">{(g.drugs || []).length} Drugs</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only" checked={!!g.enabled}
                      onChange={(e) => { e.stopPropagation(); setGroups((gs) => gs.map((x) => x.id === g.id ? { ...x, enabled: e.target.checked } : x)); api.put(`/rx-groups/${g.id}`, { name: g.name, enabled: e.target.checked, drugs: g.drugs || [] }); }} />
                    <span className={`w-9 h-5 rounded-full relative transition-colors ${g.enabled ? "bg-[#0A4C36]" : "bg-[#D6D2CB]"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${g.enabled ? "translate-x-4" : ""}`} />
                    </span>
                  </label>
                  <button onClick={(e) => { e.stopPropagation(); remove(g.id); }} className="text-red-500 hover:bg-red-50 w-8 h-8 rounded-full flex items-center justify-center" data-testid={`rx-group-delete-${g.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Right: editor */}
        <section className="md:col-span-8 card-soft p-6">
          {!active ? (
            <div className="py-24 text-center text-[#8A9992]">Create or select a group to add drugs.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <input
                  value={active.name}
                  onChange={(e) => setGroups((gs) => gs.map((x) => x.id === active.id ? { ...x, name: e.target.value } : x))}
                  onBlur={(e) => updateActive({ name: e.target.value })}
                  className="text-2xl font-semibold text-[#1F2A24] bg-transparent border-b border-transparent focus:border-[#0A4C36] outline-none"
                  style={{ fontFamily: "Instrument Sans" }}
                  data-testid="rx-active-name"
                />
                <button className="btn-emerald" onClick={saveActive} data-testid="rx-save-btn"><Save size={14} /> Save</button>
              </div>

              <div className="mb-4">
                <MedicineSearch mode="drugs" onPickMedicine={addMedicine} placeholder="Enter first 3 characters to search drugs" />
              </div>

              <div className="text-sm text-[#47554D] mb-3">Selected drugs {(active.drugs || []).length}</div>
              {(active.drugs || []).length === 0 && (
                <div className="py-16 text-center">
                  <div className="text-6xl mb-3">℞</div>
                  <div className="text-[#8A9992] text-sm">Add drug to proceed</div>
                </div>
              )}
              <div className="space-y-3">
                {(active.drugs || []).map((d, i) => (
                  <MedicationRow
                    key={i}
                    value={d}
                    index={i}
                    onChange={(nv) => setGroups((gs) => gs.map((x) => x.id === active.id ? { ...x, drugs: x.drugs.map((y, j) => j === i ? nv : y) } : x))}
                    onRemove={() => updateActive({ drugs: active.drugs.filter((_, j) => j !== i) })}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
